import { useEffect, useState } from 'react';
import {
  getDocumentReferencePreviewContent,
  type DocumentReferencePreviewContent,
} from './document-reference/document-reference-content';
import { DocumentReferenceStructuredContent } from './document-reference/DocumentReferenceStructuredContent';
import { decodeSignedUrlPreviewText } from './signedUrlPreviewUtils';

interface SignedUrlPreviewProps {
  signedUrl?: string;
  contentType?: string;
  title: string;
  unavailableLabel?: string;
}

const PDF_URL_PATTERN = /\.pdf(?:$|[?#])/i;
const IMAGE_URL_PATTERN = /\.(?:png|jpe?g|gif|webp|bmp|svg)(?:$|[?#])/i;
const IMAGE_PREVIEW_ERROR = 'Preview unavailable';
const LOADING_PREVIEW = 'Loading preview...';
const LOADING_PDF = 'Loading PDF...';

type PreviewState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; type: 'pdf'; url: string }
  | { status: 'ready'; type: 'image'; url: string }
  | { status: 'ready'; type: 'xml'; content: DocumentReferencePreviewContent };

const getImageContentTypeFromUrl = (signedUrl: string) => {
  const extension = signedUrl.match(
    /\.(png|jpe?g|gif|webp|bmp|svg)(?:$|[?#])/i,
  )?.[1];

  if (!extension) return undefined;
  if (extension.toLowerCase() === 'jpg') return 'image/jpeg';
  if (extension.toLowerCase() === 'jpeg') return 'image/jpeg';
  if (extension.toLowerCase() === 'svg') return 'image/svg+xml';
  return `image/${extension.toLowerCase()}`;
};

const isPdfPreview = (contentType?: string, signedUrl?: string) =>
  contentType?.toLowerCase().includes('pdf') ||
  PDF_URL_PATTERN.test(signedUrl ?? '');

const getPreviewType = ({
  responseContentType,
  contentType,
  signedUrl,
}: {
  responseContentType: string;
  contentType?: string;
  signedUrl: string;
}) => {
  const normalizedContentType = contentType?.toLowerCase() ?? '';

  if (
    responseContentType.includes('pdf') ||
    normalizedContentType.includes('pdf') ||
    PDF_URL_PATTERN.test(signedUrl)
  ) {
    return 'pdf';
  }

  if (
    responseContentType.startsWith('image/') ||
    normalizedContentType.startsWith('image/') ||
    IMAGE_URL_PATTERN.test(signedUrl)
  ) {
    return 'image';
  }

  if (
    responseContentType.includes('xml') ||
    responseContentType.startsWith('text/') ||
    normalizedContentType.includes('xml') ||
    normalizedContentType.startsWith('text/')
  ) {
    return 'xml';
  }

  return undefined;
};

const PreviewMessage = ({ children }: { children: string }) => (
  <p className="text-[10px] leading-tight text-zinc-500 italic">{children}</p>
);

const PdfPreviewCard = ({ title, url }: { title: string; url: string }) => (
  <a
    href={url}
    target="_blank"
    rel="noreferrer"
    aria-label={`Click to preview ${title}`}
    className="flex h-full w-full items-center justify-center px-4 text-center text-[10px] leading-tight font-medium text-blue-600 underline"
  >
    Click to preview
  </a>
);

export const SignedUrlPreview = ({
  signedUrl,
  contentType,
  title,
  unavailableLabel = 'File unavailable',
}: SignedUrlPreviewProps) => {
  const [previewState, setPreviewState] = useState<PreviewState>({
    status: 'idle',
  });
  const [hasImageError, setHasImageError] = useState(false);

  const safeSignedUrl = signedUrl?.trim();
  const loadingLabel = isPdfPreview(contentType, safeSignedUrl)
    ? LOADING_PDF
    : LOADING_PREVIEW;

  useEffect(() => {
    setHasImageError(false);
    setPreviewState({ status: 'idle' });

    if (!safeSignedUrl) return;

    const abortController = new AbortController();
    let imageBlobUrl: string | undefined;

    const loadPreview = async () => {
      setPreviewState({ status: 'loading' });

      try {
        const response = await fetch(safeSignedUrl, {
          signal: abortController.signal,
        });

        if (abortController.signal.aborted) return;
        if (!response.ok) throw new Error('Fetch failed');

        const responseContentType = (
          response.headers.get('content-type') ?? ''
        ).toLowerCase();
        const previewType = getPreviewType({
          responseContentType,
          contentType,
          signedUrl: safeSignedUrl,
        });

        if (previewType === 'pdf') {
          setPreviewState({
            status: 'ready',
            type: 'pdf',
            url: safeSignedUrl,
          });
          return;
        }

        if (previewType === 'xml') {
          const bytes = await response.arrayBuffer();
          if (abortController.signal.aborted) return;
          const text = decodeSignedUrlPreviewText(
            bytes,
            responseContentType || contentType,
          );
          setPreviewState({
            status: 'ready',
            type: 'xml',
            content: getDocumentReferencePreviewContent(text),
          });
          return;
        }

        if (previewType === 'image') {
          const blob = await response.blob();
          if (abortController.signal.aborted) return;
          imageBlobUrl = URL.createObjectURL(
            new Blob([blob], {
              type: blob.type || getImageContentTypeFromUrl(safeSignedUrl),
            }),
          );
          setPreviewState({
            status: 'ready',
            type: 'image',
            url: imageBlobUrl,
          });
          return;
        }

        throw new Error('Unsupported content type');
      } catch {
        if (!abortController.signal.aborted) {
          setPreviewState({ status: 'error' });
        }
      }
    };

    void loadPreview();

    return () => {
      abortController.abort();
      if (imageBlobUrl) URL.revokeObjectURL(imageBlobUrl);
    };
  }, [contentType, safeSignedUrl]);

  if (!safeSignedUrl) {
    return <PreviewMessage>{unavailableLabel}</PreviewMessage>;
  }

  if (previewState.status === 'loading') {
    return <PreviewMessage>{loadingLabel}</PreviewMessage>;
  }

  if (previewState.status === 'error') {
    return <PreviewMessage>{IMAGE_PREVIEW_ERROR}</PreviewMessage>;
  }

  if (previewState.status !== 'ready') {
    return <PreviewMessage>{unavailableLabel}</PreviewMessage>;
  }

  if (previewState.type === 'pdf') {
    return <PdfPreviewCard title={title} url={previewState.url} />;
  }

  if (previewState.type === 'xml') {
    if (!previewState.content.content) {
      return <PreviewMessage>{unavailableLabel}</PreviewMessage>;
    }

    return (
      <div className="w-full p-2">
        {previewState.content.kind === 'structured' ? (
          <DocumentReferenceStructuredContent
            content={previewState.content.content}
          />
        ) : (
          <p className="text-[10px] leading-tight whitespace-pre-wrap text-zinc-700">
            {previewState.content.content}
          </p>
        )}
      </div>
    );
  }

  if (hasImageError) {
    return (
      <div className="flex w-full flex-col items-center gap-2 px-4 text-center">
        <PreviewMessage>{IMAGE_PREVIEW_ERROR}</PreviewMessage>
        <a
          href={safeSignedUrl}
          target="_blank"
          rel="noreferrer"
          className="text-[10px] leading-tight text-blue-600 underline"
        >
          Open file
        </a>
      </div>
    );
  }

  return (
    <img
      src={previewState.url}
      alt={title}
      className="max-h-full w-full object-contain"
      onError={() => setHasImageError(true)}
    />
  );
};
